/* Pixel Threads - THE RECORD v1.0
   Every anomaly that ever surfaced stays visible as a ghost. Each one is its
   own generated 11x9 glyph, drawn through the same 8x8 Bayer dither the store
   already uses for RESOLVE and the tuner. Attention resolves a ghost, and its
   designation decodes out of hex noise as it does. The designation is derived
   from the glyph itself (sha1 of the grid, first six hex), so it needs no
   storage, no generator edit and no runtime AI. Nothing here is a catalogue:
   no titles, no dates, no thumbnails. The glyph is the anomaly.

   Progressive enhancement only. The section ships hidden. It shows itself
   only after /archive answers with at least one anomaly. No JS, a Worker
   outage, a CORS refusal or an empty archive all render nothing at all, so the
   page never promises a record it cannot show. */
(function () {
  'use strict';

  var root = document.querySelector('[data-pt-record]');
  if (!root || !window.fetch || !window.requestAnimationFrame) return;

  var cfg = {};
  try {
    var cfgEl = root.querySelector('[data-pt-record-config]');
    if (cfgEl) cfg = JSON.parse(cfgEl.textContent) || {};
  } catch (e) { cfg = {}; }

  var ENDPOINT = typeof cfg.endpoint === 'string' && cfg.endpoint ? cfg.endpoint : '';
  if (!ENDPOINT) return;
  var LIMIT = Math.max(1, Math.min(200, parseInt(cfg.limit, 10) || 200));
  var TOTAL = parseInt(cfg.total, 10) || 27132;
  var FORMAT = typeof cfg.format === 'string' && cfg.format ? cfg.format : '{day} / {designation} / of {total}';
  var NOTES = Array.isArray(cfg.notes) ? cfg.notes.map(function (s) { return String(s).trim(); }).filter(Boolean) : [];
  var SHOW_SOLD = !!cfg.showSold;
  var PULSE = cfg.pulse !== false;
  var IDLE = typeof cfg.idle === 'string' ? cfg.idle : '';
  var LABEL = typeof cfg.label === 'string' && cfg.label ? cfg.label : 'Anomaly {day}, designation {designation}';
  var COUNT = typeof cfg.count === 'string' && cfg.count ? cfg.count : '{n} surfaced / of {total}';
  var STORE_KEY = 'pt_record_seen';

  var field = root.querySelector('[data-pt-record-field]');
  var readCode = root.querySelector('[data-pt-record-code]');
  var readNote = root.querySelector('[data-pt-record-note]');
  var countEl = root.querySelector('[data-pt-record-count]');
  if (!field) return;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

  var BAYER = [
    0, 32, 8, 40, 2, 34, 10, 42,
    48, 16, 56, 24, 50, 18, 58, 26,
    12, 44, 4, 36, 14, 46, 6, 38,
    60, 28, 52, 20, 62, 30, 54, 22,
    3, 35, 11, 43, 1, 33, 9, 41,
    51, 19, 59, 27, 49, 17, 57, 25,
    15, 47, 7, 39, 13, 45, 5, 37,
    63, 31, 55, 23, 61, 29, 53, 21
  ];
  var HEX = '0123456789ABCDEF';
  var COLS = 11, ROWS = 9;
  var GHOST = 0.18;      /* coverage of a ghost nobody has looked at */
  var TRACE = 0.34;      /* coverage of a ghost you already resolved this visit: attention leaves a trace */
  var RISE_MS = 720;     /* attention arriving */
  var FALL_MS = 1500;    /* attention leaving */
  var PULSE_MIN = 6000, PULSE_MAX = 11000, PULSE_PEAK = 0.42, PULSE_HOLD = 420;

  var seen = {};
  try {
    var raw = window.sessionStorage.getItem(STORE_KEY);
    if (raw) JSON.parse(raw).forEach(function (d) { seen[d] = true; });
  } catch (e) {}
  function remember(day) {
    seen[day] = true;
    try { window.sessionStorage.setItem(STORE_KEY, JSON.stringify(Object.keys(seen).map(Number))); } catch (e) {}
  }

  function pad3(n) { n = String(n); while (n.length < 3) n = '0' + n; return n; }
  function fmtTotal(n) { return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
  function fill(tpl, day, des, n) {
    return tpl.replace('{day}', pad3(day)).replace('{designation}', des)
      .replace('{total}', fmtTotal(TOTAL)).replace('{n}', String(n));
  }
  function ease(t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }
  function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

  function validGrid(g) {
    if (!Array.isArray(g) || g.length !== ROWS) return false;
    for (var i = 0; i < ROWS; i++) {
      if (typeof g[i] !== 'string' || g[i].length !== COLS || /[^.#]/.test(g[i])) return false;
    }
    return true;
  }

  /* Designation: sha1(grid rows joined with nothing) -> first six hex, upper.
     Deterministic and reproducible by anyone who holds the shirt. */
  function fnv6(s) {
    var h = 0x811c9dc5;
    for (var i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
    return ('000000' + h.toString(16)).slice(-6).toUpperCase();
  }
  function designate(grid) {
    var s = grid.join('');
    if (window.crypto && window.crypto.subtle && window.TextEncoder) {
      return window.crypto.subtle.digest('SHA-1', new TextEncoder().encode(s)).then(function (buf) {
        var b = new Uint8Array(buf), out = '';
        for (var i = 0; i < 3; i++) out += HEX[b[i] >> 4] + HEX[b[i] & 15];
        return out;
      }).catch(function () { return fnv6(s); });
    }
    return Promise.resolve(fnv6(s));
  }

  /* per-glyph noise: a tiny LCG seeded by the designation so the decode is
     lively without being random from one visit to the next */
  function lcg(seed) {
    var x = (seed >>> 0) || 1;
    return function () { x = (Math.imul(x, 1664525) + 1013904223) >>> 0; return x / 4294967296; };
  }

  var glyphs = [];
  var active = null;      /* the glyph holding attention (hover / focus) */
  var locked = null;      /* the glyph you tapped or clicked, holds after you leave */
  var animating = false;
  var lastT = 0;
  var cellPx = 4;
  var dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));

  function readCell() {
    var v = parseFloat(window.getComputedStyle(field).getPropertyValue('--pt-record-cell'));
    cellPx = v > 0 ? v : 4;
  }

  function Glyph(item, des) {
    this.day = item.day;
    this.grid = item.grid;
    this.sold = !!item.sold;
    this.des = des;
    this.floor = seen[this.day] ? TRACE : GHOST;
    this.c = reduce ? 1 : this.floor;
    this.target = this.c;
    this.from = this.c;
    this.t0 = 0;
    this.dur = RISE_MS;
    this.rand = lcg(parseInt(des, 16));
    this.note = NOTES.length ? NOTES[parseInt(des, 16) % NOTES.length] : '';
    this.li = null; this.btn = null; this.canvas = null; this.ctx = null;
    this.drawnC = -1;
  }

  Glyph.prototype.mount = function () {
    var li = document.createElement('li');
    li.className = 'pt-record__cell';
    /* jitter derived from the designation so the scatter is stable */
    var jx = ((parseInt(this.des.slice(0, 2), 16) / 255) - 0.5) * 14;
    var jy = ((parseInt(this.des.slice(2, 4), 16) / 255) - 0.5) * 12;
    li.style.setProperty('--jx', jx.toFixed(1) + 'px');
    li.style.setProperty('--jy', jy.toFixed(1) + 'px');

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pt-record__glyph';
    btn.setAttribute('aria-label', fill(LABEL, this.day, this.des, 0));
    btn.setAttribute('aria-pressed', 'false');
    var canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    btn.appendChild(canvas);
    li.appendChild(btn);

    this.li = li; this.btn = btn; this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.size();

    var self = this;
    btn.addEventListener('pointerenter', function (e) { if (e.pointerType !== 'touch') attend(self); });
    btn.addEventListener('pointerleave', function (e) { if (e.pointerType !== 'touch') release(self); });
    btn.addEventListener('focus', function () { attend(self); });
    btn.addEventListener('blur', function () { release(self); });
    btn.addEventListener('click', function () { toggleLock(self); });
    return li;
  };

  Glyph.prototype.size = function () {
    var w = COLS * cellPx, h = ROWS * cellPx;
    this.canvas.style.width = w + 'px';
    this.canvas.style.height = h + 'px';
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    this.drawnC = -1;
    this.draw();
  };

  Glyph.prototype.draw = function () {
    var ctx = this.ctx;
    if (!ctx) return;
    var c = this.c;
    if (c === this.drawnC) return;
    this.drawnC = c;
    var px = cellPx * dpr;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    var alpha = 0.5 + 0.5 * c;
    var hollow = SHOW_SOLD && !this.sold && c > 0.85;
    ctx.fillStyle = 'rgba(244,244,239,' + alpha.toFixed(3) + ')';
    for (var y = 0; y < ROWS; y++) {
      var row = this.grid[y];
      for (var x = 0; x < COLS; x++) {
        if (row.charCodeAt(x) !== 35) continue; /* '#' */
        var thr = BAYER[(x & 7) + ((y & 7) << 3)] / 64;
        if (c <= thr) continue;
        var X = x * px, Y = y * px;
        if (hollow) {
          var s = Math.max(1, Math.round(dpr));
          ctx.fillRect(X, Y, px, s); ctx.fillRect(X, Y + px - s, px, s);
          ctx.fillRect(X, Y, s, px); ctx.fillRect(X + px - s, Y, s, px);
        } else {
          ctx.fillRect(X, Y, px, px);
        }
      }
    }
  };

  /* the readout: designation decodes out of hex noise as coverage rises */
  Glyph.prototype.decode = function () {
    var out = '', c = this.c;
    for (var k = 0; k < 6; k++) {
      var gate = (k + 1) / 7;
      out += (c >= gate || reduce) ? this.des.charAt(k) : HEX[Math.floor(this.rand() * 16)];
    }
    return out;
  };

  function setTarget(g, v, dur) {
    v = clamp01(v);
    if (Math.abs(g.target - v) < 0.001 && Math.abs(g.c - v) < 0.001) return;
    g.from = g.c; g.target = v; g.t0 = 0; g.dur = dur;
    kick();
  }

  function focusOf() { return active || locked; }

  function attend(g) {
    active = g;
    setTarget(g, 1, RISE_MS);
    showReadout();
  }
  function release(g) {
    if (active === g) active = null;
    if (locked !== g) setTarget(g, g.floor, FALL_MS);
    showReadout();
  }
  function toggleLock(g) {
    if (locked === g) {
      locked = null;
      g.btn.setAttribute('aria-pressed', 'false');
      if (active !== g) setTarget(g, g.floor, FALL_MS);
    } else {
      if (locked) {
        var old = locked;
        old.btn.setAttribute('aria-pressed', 'false');
        if (active !== old) setTarget(old, old.floor, FALL_MS);
      }
      locked = g;
      g.btn.setAttribute('aria-pressed', 'true');
      setTarget(g, 1, RISE_MS);
    }
    showReadout();
  }

  function showReadout() {
    var g = focusOf();
    if (!readCode) return;
    if (!g) {
      readCode.textContent = IDLE;
      readCode.classList.remove('is-locked');
      if (readNote) { readNote.textContent = ''; readNote.classList.remove('is-on'); }
      return;
    }
    var full = g.c >= 0.999;
    readCode.textContent = fill(FORMAT, g.day, g.decode(), 0);
    readCode.classList.toggle('is-locked', full);
    if (readNote) {
      if (full && g.note) { readNote.textContent = g.note; readNote.classList.add('is-on'); }
      else { readNote.classList.remove('is-on'); }
    }
    if (full && !seen[g.day]) { remember(g.day); g.floor = TRACE; }
  }

  function kick() {
    if (animating) return;
    animating = true;
    lastT = 0;
    window.requestAnimationFrame(tick);
  }

  function tick(now) {
    var busy = false;
    for (var i = 0; i < glyphs.length; i++) {
      var g = glyphs[i];
      if (Math.abs(g.c - g.target) < 0.0005) { if (g.c !== g.target) { g.c = g.target; g.draw(); } continue; }
      if (!g.t0) g.t0 = now;
      var p = clamp01((now - g.t0) / g.dur);
      g.c = g.from + (g.target - g.from) * ease(p);
      if (p >= 1) g.c = g.target;
      g.draw();
      if (p < 1) busy = true;
    }
    var f = focusOf();
    if (f) showReadout();
    if (busy) window.requestAnimationFrame(tick);
    else animating = false;
  }

  /* ambient: every so often one ghost stirs, as if the static remembered it.
     only while the field is on screen, never under reduced motion. */
  function startPulse() {
    if (!PULSE || reduce || !('IntersectionObserver' in window)) return;
    var inView = false, timer = null;
    function schedule() {
      clearTimeout(timer);
      timer = setTimeout(function () {
        if (inView && glyphs.length) {
          var pool = glyphs.filter(function (g) { return g !== active && g !== locked; });
          if (pool.length) {
            var g = pool[Math.floor(Math.random() * pool.length)];
            setTarget(g, PULSE_PEAK, 260);
            setTimeout(function () { if (g !== active && g !== locked) setTarget(g, g.floor, 900); }, PULSE_HOLD);
          }
        }
        schedule();
      }, PULSE_MIN + Math.random() * (PULSE_MAX - PULSE_MIN));
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { inView = en.isIntersecting; });
    }, { threshold: 0.1 });
    io.observe(field);
    schedule();
  }

  function build(items) {
    readCell();
    var frag = document.createDocumentFragment();
    return Promise.all(items.map(function (it) { return designate(it.grid); })).then(function (dess) {
      items.forEach(function (it, i) {
        var g = new Glyph(it, dess[i]);
        glyphs.push(g);
        frag.appendChild(g.mount());
      });
      field.appendChild(frag);
      if (countEl) countEl.textContent = fill(COUNT, 0, '', glyphs.length);
      if (readCode) readCode.textContent = IDLE;
      root.hidden = false;
      root.classList.add('is-live');
      startPulse();

      var rt = null;
      window.addEventListener('resize', function () {
        clearTimeout(rt);
        rt = setTimeout(function () {
          var before = cellPx; readCell();
          var d = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
          if (before !== cellPx || d !== dpr) { dpr = d; glyphs.forEach(function (g) { g.size(); }); }
        }, 120);
      });
    });
  }

  var url = ENDPOINT + (ENDPOINT.indexOf('?') === -1 ? '?' : '&') + 'limit=' + LIMIT;
  fetch(url, { mode: 'cors', credentials: 'omit' })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (data) {
      var items = data && Array.isArray(data.anomalies) ? data.anomalies : [];
      items = items.filter(function (it) { return it && typeof it.day === 'number' && it.day > 0 && validGrid(it.grid); });
      /* oldest first reads as a record; the Worker returns newest first */
      items.sort(function (a, b) { return a.day - b.day; });
      if (!items.length) return;
      return build(items);
    })
    .catch(function () { /* the section stays hidden: nothing to promise */ });
})();
